/** Household plan codes from payment catalog (BASE | CARE | FAMILY). */
export type PlanCode = 'BASE' | 'CARE' | 'FAMILY';

export type PlanUiId = 'base' | 'care' | 'family';

export interface PaymentPlanLimitMeter {
  subject: string;
  period: string;
}

export interface PaymentPlanLimit {
  key: string;
  state: 'disabled' | 'limited' | 'unlimited' | string;
  value: number;
  meter?: PaymentPlanLimitMeter;
}

export interface PaymentPlanScope {
  scopeKey: string;
  limitValue: number;
  unit?: string;
  resetPeriod?: string;
}

export interface PaymentPlan {
  id: string;
  code: string;
  displayName: string;
  billingKind: string;
  interval: string;
  amountCents: number;
  currency: string;
  stripePriceId: string;
  isPublic: boolean;
  isRecommended: boolean;
  sortOrder: number;
  description: string[];
  scopes?: PaymentPlanScope[];
  limits?: PaymentPlanLimit[];
}

export interface FamilyEntitlement {
  familyId: string;
  planCode: string;
  status: string;
  currentPeriodEnd: number;
  source: string;
  scopes?: Record<string, number>;
  usage?: Record<string, number>;
  /** Remaining boost credits by bank key (e.g. ai.requests.bank). */
  boosts?: Record<string, number>;
}

export interface PaymentsConfig {
  paymentsEnabled: boolean;
  quotasEnforced: boolean;
  /** One-time AI/duty boost packs (CARE+). */
  addonsEnabled?: boolean;
}

/** Catalog row from GET /payments/addons */
export interface PaymentAddon {
  code: string;
  displayName: string;
  description: string;
  grantBankKey: string;
  grantAmount: number;
  amountCents: number;
  currency: string;
  stripePriceId: string;
}

export const ADDON_CODE = {
  AI_BOOST_10: 'AI_BOOST_10',
  AI_BOOST_50: 'AI_BOOST_50',
  DUTY_BOOST_5: 'DUTY_BOOST_5',
} as const;

const LEGACY_TO_UI: Record<string, PlanUiId> = {
  base: 'base',
  basic: 'base',
  starter: 'base',
  care: 'care',
  professional: 'care',
  gold: 'care',
  family: 'family',
  platinum: 'family',
};

export function toPlanUiId(raw: string | null | undefined): PlanUiId {
  if (!raw) return 'base';
  return LEGACY_TO_UI[raw.trim().toLowerCase()] ?? 'base';
}

export function toPlanCode(uiId: PlanUiId): PlanCode {
  return uiId.toUpperCase() as PlanCode;
}
